import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { useAlertHistory, useMarkAlertRead } from '@/hooks/queries/useAlerts';
import { formatRelativeTime, formatAlertType } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { AlertSeverity } from '@steward/shared';

export function RecentAlerts() {
  const { data: alerts, isLoading } = useAlertHistory({ limit: 5 });
  const markRead = useMarkAlertRead();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Alerts</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const recentAlerts = alerts || [];
  const unreadCount = recentAlerts.filter((a) => !a.isRead).length;

  const severityVariants: Record<AlertSeverity, 'info' | 'warning' | 'error'> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <CardTitle>Recent Alerts</CardTitle>
          {unreadCount > 0 && (
            <Badge variant="error" size="sm">
              {unreadCount}
            </Badge>
          )}
        </div>
        <Link to="/alerts/history" className="text-sm text-brand-600 hover:text-brand-700">
          View all
        </Link>
      </CardHeader>
      <CardContent>
        {recentAlerts.length === 0 ? (
          <EmptyState
            icon={<NoDataIcon />}
            title="No alerts"
            description="You're all caught up"
          />
        ) : (
          <div className="space-y-3">
            {recentAlerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  'p-3 rounded-lg border transition-colors',
                  alert.isRead
                    ? 'border-gray-200 bg-white'
                    : 'border-brand-200 bg-brand-50'
                )}
                onClick={() => {
                  if (!alert.isRead) {
                    markRead.mutate(alert.id);
                  }
                }}
                role={!alert.isRead ? 'button' : undefined}
              >
                {/* Badge and time share one line; the text below gets the card's full width. At
                    four-across this card is ~270px, where a third column for the time squeezed
                    the badge onto two lines and the title down to "Consider ro…". */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant={severityVariants[alert.severity]} size="sm" className="whitespace-nowrap">
                      {formatAlertType(alert.alertType)}
                    </Badge>
                    {!alert.isRead && (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-brand-600" />
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatRelativeTime(alert.createdAt)}
                  </span>
                </div>
                <p data-testid="alert-title" className="text-sm font-medium text-gray-900 line-clamp-2">
                  {alert.tradeChainId ? (
                    <Link to={`/chains/${alert.tradeChainId}`} className="hover:underline">
                      {alert.title}
                    </Link>
                  ) : (
                    alert.title
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.message}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
